// frontend/src/pages/RecipeEdit.js
// import { dividerClasses } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { useAuth } from '../context/AuthContext';
import supabase from '../supabaseClient';
import { useSupabaseRecipeImage, useAddRecipeImage } from '../hooks/getRecipeImage';

const styles = {
  recipeEditContainer: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
  },
  recipeEditTitle: {
    color: '#333',
    marginBottom: '2rem',
    fontSize: '2rem',
  },
  recipeEditForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
  },
  formSection: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  sectionTitle: {
    color: '#52cd92',
    marginBottom: '1.5rem',
    fontSize: '1.5rem',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  formInput: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    transition: 'border-color 0.2s',
  },
  nutritionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
  },
  checkboxGroup: {
    display: 'flex',
    gap: '2rem',
    marginBottom: '1.5rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
  },
  ingredientsList: {
    marginBottom: '1.5rem',
  },
  ingredientItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem',
    background: '#f8f9fa',
    borderRadius: '4px',
    marginBottom: '0.5rem',
  },
  ingredientSearchContainer: {
    position: 'relative',
    marginBottom: '1rem',
  },
  selectedIngredient: {
    marginBottom: '10px',
    padding: '8px 12px',
    backgroundColor: 'rgba(82, 205, 146, 0.1)',
    borderRadius: '4px',
    border: '1px solid rgba(82, 205, 146, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  searchInput: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
  },
  searchResults: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    maxHeight: '200px',
    overflowY: 'auto',
    background: 'white',
    border: '1px solid #ddd',
    borderRadius: '4px',
    zIndex: 1000,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  searchResultItem: {
    padding: '0.75rem',
    cursor: 'pointer',
    borderBottom: '1px solid #eee',
    '&:hover': {
      backgroundColor: 'rgba(82, 205, 146, 0.1)',
    },
  },
  searchResultItemSelected: {
    backgroundColor: 'rgba(82, 205, 146, 0.1)',
  },
  noResults: {
    padding: '0.75rem',
    color: '#666',
  },
  ingredientForm: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr auto',
    gap: '1rem',
    alignItems: 'start',
  },
  stepForm: {
    display: 'grid',
    gridTemplateColumns: '0.5fr 1fr auto',
    gap: '1rem',
    alignItems: 'start',
  },
  stepsList: {
    margin: 0,
    padding: 0,
    marginBottom: '1.5rem',
    listStyle: 'none',
  },
  stepItem: {
    marginBottom: '1rem',
    padding: '0.75rem',
    background: '#f8f9fa',
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  categoriesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '2rem',
  },
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '0.75rem',
  },
  formActions: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    marginTop: '2rem',
  },
  button: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      opacity: 0.9,
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  updateButton: {
    backgroundColor: '#52cd92',
    color: 'white',
  },
  saveButton: {
    backgroundColor: '#52cd92',
    color: 'white',
  },
  addButton: {
    backgroundColor: '#52cd92',
    color: 'white',
    whiteSpace: 'nowrap',
    padding: '0.75rem',
    border: '1px solid #52cd92',
    borderRadius: '4px',
    fontSize: '1rem',
  },
  removeButton: {
    backgroundColor: '#ff4d4d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
  clearButton: {
    background: 'none',
    border: 'none',
    color: '#666',
    cursor: 'pointer',
    padding: '4px 8px',
    fontSize: '1.25rem',
  },
  successMessage: {
    color: '#52cd92',
    margin: 0,
    fontWeight: 500,
  },
  selectionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.75rem 0.5rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#e0e0e0',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: 'white',
    width: '100%',
    fontSize: '1rem',
    color: '#333',
  },
  selectedButton: {
    backgroundColor: 'rgba(82, 205, 146, 0.1)',
    borderColor: '#52cd92',
  },
  buttonIcon: {
    width: '24px',
    height: '24px',
    objectFit: 'contain',
  },
};

function RecipeEdit() {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [recipeName, setRecipeName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [isVegetarian, setIsVegetarian] = useState(false);
  const [isVegan, setIsVegan] = useState(false);
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [carbohydrate, setCarbohydrate] = useState('');
  const [fat, setFat] = useState('');
  const [costPerServing, setCostPerServing] = useState('');
  const [ingredients, setIngredients] = useState([]);
  const [allIngredients, setAllIngredients] = useState([]);
  const [steps, setSteps] = useState([]);
  const [newIngredient, setNewIngredient] = useState({ 
    ingredient_id: '', 
    ingredient_name: '', 
    quantity: '', 
    notes: '',
    unit_of_measurement: '' 
  });
  const [newStep, setNewStep] = useState({ 
    stepNumber: '', 
    instruction: '',
    id: '' 
  });
  const [cuisines, setCuisines] = useState([]);
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [initialCuisines, setInitialCuisines] = useState([]);
  const [diets, setDiets] = useState([]);
  const [selectedDiets, setSelectedDiets] = useState([]);
  const [initialDiets, setInitialDiets] = useState([]);
  const [mealTimes, setMealTimes] = useState([]);
  const [selectedMealTimes, setSelectedMealTimes] = useState([]);
  const [initialMealTimes, setInitialMealTimes] = useState([]);
  const [foodTypes, setFoodTypes] = useState([]);
  const [selectedFoodTypes, setSelectedFoodTypes] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [initialTags, setInitialTags] = useState([]);
  const [initialFoodTypes, setInitialFoodTypes] = useState([]);
  const [changesSavedMessage, setChangesSavedMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSelectedIngredient, setTempSelectedIngredient] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  const [currentImageSrc, setCurrentImageSrc] = useState(`/images/recipeImages/${recipeId}.jpg?_t=${Date.now()}`);
  const [selectedFile, setSelectedFile] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const supabaseImageUrl = useSupabaseRecipeImage(recipeId, refreshTrigger);

  // AI Image Generation state
  const [isGeneratingAIImage, setIsGeneratingAIImage] = useState(false);
  const [aiImagePreview, setAiImagePreview] = useState(null);
  const [showAIImageModal, setShowAIImageModal] = useState(false);
  const [isUploadingAIImage, setIsUploadingAIImage] = useState(false);
  const [aiImageError, setAiImageError] = useState(null);
  const [imageNote, setImageNote] = useState('');

  // Update currentImageSrc when supabaseImageUrl changes
  useEffect(() => {
    if (supabaseImageUrl) {
      console.log('Updating image source to:', supabaseImageUrl);
      setCurrentImageSrc(supabaseImageUrl);
    }
  }, [supabaseImageUrl]);
  const { uploadImage, isUploading, error: uploadError } = useAddRecipeImage();

  // Check recipe ownership before loading the edit page
  useEffect(() => {
    //get session access token
    const checkRecipeOwnership = async () => {
      try {
        //get session access token
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('No valid session found');
        }

        setIsLoading(true);
        const response = await fetch('/api/v1/recipe/my-recipes', {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });
        const data = await response.json();
        
        // Convert recipeId to number for comparison since URL params are strings
        const recipeIdNum = parseInt(recipeId, 10);
        const userOwnsRecipe = data.some(recipe => recipe.id === recipeIdNum);

        if (!userOwnsRecipe) {
          navigate('/');
          return;
        }
        setIsLoading(false);
      } catch (error) {
        console.error('Error checking recipe ownership:', error);
        alert('Error verifying recipe ownership');
        navigate('/');
      }
    };

    checkRecipeOwnership();
  }, [recipeId, navigate]);
  

  // State for session token
  const [sessionToken, setSessionToken] = useState('');

  // First useEffect to get the session token
  useEffect(() => {
    const getSessionToken = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          setSessionToken(session.access_token);
        } else {
          console.error('No session token available');
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error getting session:', error);
        setIsLoading(false);
      }
    };
    getSessionToken();
  }, []);

  // Second useEffect for API calls that need both session token and recipeId
  useEffect(() => {
    if (!sessionToken || !recipeId) {
      return;
    }

    const checkIsAdmin = async () => {
      const response = await fetchWithCSRF('/api/v1/users/check_user', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      const data = await response.json();
      setIsAdmin(data.isAdmin);
    }

    const fetchRecipeData = async () => {
      try {
        // Fetch recipe details
        const recipeResponse = await fetchWithCSRF(`/api/v1/recipe/${recipeId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        });
        const recipeData = await recipeResponse.json();
        if (recipeData.length > 0) {
          const recipe = recipeData[0];
          setRecipeName(recipe.name || '');
          setImageUrl(recipe.image_url || '');
          setIsVegetarian(recipe.is_vegetarian || false);
          setIsVegan(recipe.is_vegan || false);
          setCalories(recipe.calories?.toString() || '');
          setProtein(recipe.protein?.toString() || '');
          setCarbohydrate(recipe.carbohydrate?.toString() || '');
          setFat(recipe.fat?.toString() || '');
          setCostPerServing(recipe.cost_per_serving?.toString() || '');
        }

        // Fetch ingredients
        const ingredientsResponse = await fetchWithCSRF(`/api/v1/recipe-ingredient/recipe/${recipeId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        });
        const ingredientsData = await ingredientsResponse.json();
        if (Array.isArray(ingredientsData)) {
          setIngredients(ingredientsData);
        }

        // Fetch all ingredients
        const allIngredientsResponse = await fetchWithCSRF('/api/v1/ingredient', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        });
        const allIngredientsData = await allIngredientsResponse.json();
        if (Array.isArray(allIngredientsData)) {
          setAllIngredients(allIngredientsData);
        } else {
          console.error('Expected array of ingredients, got:', allIngredientsData);
          setAllIngredients([]);
        }

        // Fetch steps
        const stepsResponse = await fetchWithCSRF(`/api/v1/recipe-step/${recipeId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        });
        const stepsData = await stepsResponse.json();
        if (Array.isArray(stepsData)) {
          setSteps(stepsData);
        }

        // Fetch cuisines
        const [cuisinesResponse, recipeCuisinesResponse] = await Promise.all([
          fetchWithCSRF('/api/v1/cuisine', {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${sessionToken}` }
          }),
          fetchWithCSRF(`/api/v1/recipe-cuisine/recipe/${recipeId}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${sessionToken}` }
          })
        ]);

        const cuisinesData = await cuisinesResponse.json();
        const recipeCuisinesData = await recipeCuisinesResponse.json();

        if (Array.isArray(cuisinesData)) {
          setCuisines(cuisinesData);
        }
        if (Array.isArray(recipeCuisinesData)) {
          const cuisineIds = recipeCuisinesData.map(item => item.cuisine_id);
          setSelectedCuisines(cuisineIds);
          setInitialCuisines(cuisineIds);
        }

        // Fetch diets
        const [dietsResponse, recipeDietsResponse] = await Promise.all([
          fetchWithCSRF('/api/v1/diet', {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${sessionToken}` }
          }),
          fetchWithCSRF(`/api/v1/recipe-diet/recipe/${recipeId}/diets`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${sessionToken}` }
          })
        ]);

        const dietsData = await dietsResponse.json();
        const recipeDietsData = await recipeDietsResponse.json();

        if (Array.isArray(dietsData)) {
          setDiets(dietsData);
        }
        if (Array.isArray(recipeDietsData)) {
          const dietIds = recipeDietsData.map(item => item.diet_id);
          setSelectedDiets(dietIds);
          setInitialDiets(dietIds);
        }

        // Fetch meal times
        const [mealTimesResponse, recipeMealTimesResponse] = await Promise.all([
          fetchWithCSRF('/api/v1/recipe-time/enum', {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${sessionToken}` }
          }),
          fetchWithCSRF(`/api/v1/recipe-time/recipe/${recipeId}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${sessionToken}` }
          })
        ]);

        const mealTimesData = await mealTimesResponse.json();
        const recipeMealTimesData = await recipeMealTimesResponse.json();

        if (Array.isArray(mealTimesData)) {
          const formattedData = mealTimesData.map(item => ({
            id: item.value,
            name: item.value,
            value: item.value
          }));
          setMealTimes(formattedData);
        }
        if (Array.isArray(recipeMealTimesData)) {
          const mealTimeValues = recipeMealTimesData.map(item => item.time);
          setSelectedMealTimes(mealTimeValues);
          setInitialMealTimes(mealTimeValues);
        }

        // Fetch food types
        const [foodTypesResponse, recipeFoodTypesResponse] = await Promise.all([
          fetchWithCSRF('/api/v1/food-type', {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${sessionToken}` }
          }),
          fetchWithCSRF(`/api/v1/recipe-food-type/recipe/${recipeId}/food_types`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${sessionToken}` }
          })
        ]);

        const foodTypesData = await foodTypesResponse.json();
        const recipeFoodTypesData = await recipeFoodTypesResponse.json();

        if (Array.isArray(foodTypesData)) {
          setFoodTypes(foodTypesData);
        }
        if (Array.isArray(recipeFoodTypesData)) {
          const foodTypeIds = recipeFoodTypesData.map(item => item.food_type_id);
          setSelectedFoodTypes(foodTypeIds);
          setInitialFoodTypes(foodTypeIds);
        }

        // Fetch tags
        const tagsResponse = await fetchWithCSRF('/api/v1/tag/default', {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        });
        const tagsData = await tagsResponse.json();
        if (Array.isArray(tagsData)) {
          setTags(tagsData);
        }
        
        // Fetch recipe tags
        const recipeTagsResponse = await fetchWithCSRF(`/api/v1/recipe-tag/recipe/${recipeId}`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        });
        const recipeTagsData = await recipeTagsResponse.json();
        if (Array.isArray(recipeTagsData)) {
          const tagIds = recipeTagsData.map(item => item.tag_id);
          setSelectedTags(tagIds);
          setInitialTags(tagIds);
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching recipe data:', error);
        setIsLoading(false);
      }
    };

    fetchRecipeData();
    checkIsAdmin();
  }, [sessionToken, recipeId]);


  const updateRecipe = () => {
    const updatedRecipe = {
      name: recipeName,
      image_url: imageUrl,
      is_vegetarian: isVegetarian,
      is_vegan: isVegan,
      calories: parseFloat(calories),
      protein: parseFloat(protein),
      carbohydrate: parseFloat(carbohydrate),
      fat: parseFloat(fat),
      cost_per_serving: parseFloat(costPerServing),
    };

    fetchWithCSRF(`/api/v1/recipe/${recipeId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedRecipe),
    })
      .then(response => response.json())
      .then(() => {
        console.log('Recipe updated successfully');
      })
      .catch(error => console.error('Error updating recipe:', error));
  };

  const addIngredient = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No valid session found');
    }

    fetchWithCSRF('/api/v1/recipe-ingredient', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
      body: JSON.stringify({ ...newIngredient, recipe_id: recipeId }),
    })
      .then(response => response.json())
      .then(() => {
        // Refresh the ingredients list from the server
        return fetchWithCSRF(`/api/v1/recipe-ingredient/recipe/${recipeId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });
      })
      .then(response => response.json())
      .then(data => {
        setIngredients(data);
        setNewIngredient({ 
          ingredient_id: '', 
          ingredient_name: '', 
          quantity: '', 
          notes: '',
          unit_of_measurement: ''
        });
      })
      .catch(error => console.error('Error adding ingredient:', error));
  };

  const removeIngredient = async (ingredientId) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No valid session found');
    }

    fetchWithCSRF(`/api/v1/recipe-ingredient/recipe/${recipeId}/ingredient/${ingredientId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${session.access_token}` },
    })
      .then(() => {
        setIngredients(ingredients.filter(ingredient => ingredient.ingredient_id !== ingredientId));
      })
      .catch(error => console.error('Error removing ingredient:', error));
  };

  // Helper function to get the next step number
  const getNextStepNumber = () => {
    if (steps.length === 0) return 1;
    const maxStepNumber = Math.max(...steps.map(step => step.step_number));
    return maxStepNumber + 1;
  };

  const addStep = async () => {
    const stepNumber = parseInt(newStep.stepNumber || getNextStepNumber(), 10);
    if (isNaN(stepNumber) || !newStep.instruction?.trim()) {
      console.error('Invalid step number or instruction');
      return;
    }

    // Get the current session for the auth token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No valid session found');
    }

    fetchWithCSRF('/api/v1/recipe-step', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ step_number: stepNumber, instruction: newStep.instruction, recipe_id: recipeId }),
    })
      .then(response => response.json())
      .then((data) => {
        // Add the new step with the id from the response
        setSteps([...steps, { 
          id: data.id, // Include the id from the API response
          step_number: stepNumber,
          instruction: newStep.instruction 
        }]);
        setNewStep({ stepNumber: '', instruction: '' });
      })
      .catch(error => console.error('Error adding step:', error));
  };

  const removeStep = async (stepId) => {
    // Get the current session for the auth token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      console.error('No access token available');
      return;
    }

    fetchWithCSRF(`/api/v1/recipe-step/${stepId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${session.access_token}`
      },
    })
      .then(() => {
        setSteps(steps.filter(step => step.id !== stepId));
      })
      .catch(error => console.error('Error removing step:', error));
  };

  const handleIngredientChange = (selectedOption) => {
    if (selectedOption) {
      setNewIngredient({ 
        ...newIngredient, 
        ingredient_id: selectedOption.value, 
        ingredient_name: selectedOption.label 
      });
    } else {
      setNewIngredient({ 
        ingredient_id: '', 
        ingredient_name: '', 
        quantity: '', 
        notes: '' 
      });
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImageUpload = async () => {
    if (selectedFile) {
      const success = await uploadImage(recipeId, selectedFile);
      if (success) {
        // Trigger a refresh of the Supabase image
        setRefreshTrigger(prev => prev + 1);
        // Clear the selected file after successful upload
        setSelectedFile(null);
      }
    }
  };

  // Generate AI Image using Gemini Imagen API
  const handleGenerateAIImage = async () => {
    setIsGeneratingAIImage(true);
    setAiImageError(null);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No valid session found');
      }

      const response = await fetchWithCSRF('/api/v1/recipe/generate-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          recipe_name: recipeName,
          ingredients: ingredients,
          steps: steps,
          image_note: imageNote
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate image');
      }

      const data = await response.json();
      setAiImagePreview(data.image_base64);
      setShowAIImageModal(true);
    } catch (error) {
      console.error('Error generating AI image:', error);
      setAiImageError(error.message);
    } finally {
      setIsGeneratingAIImage(false);
    }
  };

  // Upload the approved AI image to S3
  const handleApproveAIImage = async () => {
    if (!aiImagePreview) return;
    
    setIsUploadingAIImage(true);
    setAiImageError(null);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No valid session found');
      }

      const response = await fetchWithCSRF('/api/v1/recipe/upload-ai-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          recipe_id: recipeId,
          image_base64: aiImagePreview
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload image');
      }

      const data = await response.json();
      console.log('AI image uploaded to S3:', data.image_url);
      
      // Update the displayed image
      setCurrentImageSrc(data.image_url + '?_t=' + Date.now());
      
      // Close modal and clear preview
      setShowAIImageModal(false);
      setAiImagePreview(null);
    } catch (error) {
      console.error('Error uploading AI image:', error);
      setAiImageError(error.message);
    } finally {
      setIsUploadingAIImage(false);
    }
  };

  // Close the AI image modal
  const handleCloseAIImageModal = () => {
    setShowAIImageModal(false);
    setAiImagePreview(null);
    setAiImageError(null);
  };

  const handleCuisineChange = (e) => {
    const cuisineId = parseInt(e.target.value, 10);
    if (e.target.checked) {
      setSelectedCuisines([...selectedCuisines, cuisineId]);
    } else {
      setSelectedCuisines(selectedCuisines.filter(id => id !== cuisineId));
    }
  };

  const handleDietChange = (e) => {
    const dietId = parseInt(e.target.value, 10);
    if (e.target.checked) {
      setSelectedDiets([...selectedDiets, dietId]);
    } else {
      setSelectedDiets(selectedDiets.filter(id => id !== dietId));
    }
  };

  const handleMealTimeChange = (e) => {
    const mealTimeValue = e.target.value;
    if (e.target.checked) {
      setSelectedMealTimes([...selectedMealTimes, mealTimeValue]);
    } else {
      setSelectedMealTimes(selectedMealTimes.filter(value => value !== mealTimeValue));
    }
  };

  const handleFoodTypeChange = (e) => {
    const foodTypeId = parseInt(e.target.value, 10);
    if (e.target.checked) {
      setSelectedFoodTypes([...selectedFoodTypes, foodTypeId]);
    } else {
      setSelectedFoodTypes(selectedFoodTypes.filter(id => id !== foodTypeId));
    }
  };

  const handleTagChange = (e) => {
    const tagId = e.target.value; // Remove parseInt since tag IDs are UUIDs
    const isChecked = e.target.checked;
    console.log("handleTagChange called with:", {
      tagId,
      isChecked,
      currentSelectedTags: selectedTags,
      willAdd: isChecked && !selectedTags.includes(tagId),
      willRemove: !isChecked && selectedTags.includes(tagId)
    });
    
    if (isChecked) {
      setSelectedTags(prev => [...prev, tagId]);
    } else {
      setSelectedTags(prev => prev.filter(id => id !== tagId));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get the current session for the auth token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('No valid session found');
    }

    // Determine which cuisines to add and which to delete
    const cuisinesToAdd = selectedCuisines.filter(id => !initialCuisines.includes(id));
    const cuisinesToDelete = initialCuisines.filter(id => !selectedCuisines.includes(id));

    // Determine which diets to add and which to delete
    const dietsToAdd = selectedDiets.filter(id => !initialDiets.includes(id));
    const dietsToDelete = initialDiets.filter(id => !selectedDiets.includes(id));

    // Determine which meal times to add and which to delete
    const mealTimesToAdd = selectedMealTimes.filter(time => !initialMealTimes.includes(time));
    const mealTimesToDelete = initialMealTimes.filter(time => !selectedMealTimes.includes(time));

    // Determine which food types to add and which to delete
    const foodTypesToAdd = selectedFoodTypes.filter(id => !initialFoodTypes.includes(id));
    const foodTypesToDelete = initialFoodTypes.filter(id => !selectedFoodTypes.includes(id));

    // Determine which tags to add and which to delete
    const tagsToAdd = selectedTags.filter(id => !initialTags.includes(id));
    const tagsToDelete = initialTags.filter(id => !selectedTags.includes(id));

    // Create promises for adding new cuisine associations
    const addCuisinePromises = cuisinesToAdd.map(cuisineId =>
      fetchWithCSRF('/api/v1/recipe-cuisine', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ recipe_id: parseInt(recipeId), cuisine_id: cuisineId }),
      })
    );

    // Create promises for deleting cuisine associations
    const deleteCuisinePromises = cuisinesToDelete.map(cuisineId =>
      fetchWithCSRF(`/api/v1/recipe-cuisine/${recipeId}/${cuisineId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
      })
    );

    // Create promises for adding new diet associations
    const addDietPromises = dietsToAdd.map(dietId =>
      fetchWithCSRF('/api/v1/recipe-diet', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ recipe_id: parseInt(recipeId), diet_id: dietId }),
      })
    );

    // Create promises for deleting diet associations
    const deleteDietPromises = dietsToDelete.map(dietId =>
      fetchWithCSRF(`/api/v1/recipe-diet/recipe/${recipeId}/diet/${dietId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
      })
    );

    // Create promises for adding new meal time associations
    const addMealTimePromises = mealTimesToAdd.map(mealTime =>
      fetchWithCSRF('/api/v1/recipe-time', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
         },
        body: JSON.stringify({ recipe_id: parseInt(recipeId), time: mealTime }),
      })
    );

    // Create promises for deleting meal time associations
    const deleteMealTimePromises = mealTimesToDelete.map(mealTime =>
      fetchWithCSRF(`/api/v1/recipe-time/deleteByRecipeIdAndTime/${recipeId}/${mealTime}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
      })
    );

    // Create promises for adding new food type associations
    const addFoodTypePromises = foodTypesToAdd.map(foodTypeId =>
      fetchWithCSRF('/api/v1/recipe-food-type', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ recipe_id: parseInt(recipeId), food_type_id: foodTypeId }),
      })
    );

    // Create promises for deleting food type associations
    const deleteFoodTypePromises = foodTypesToDelete.map(foodTypeId =>
      fetchWithCSRF(`/api/v1/recipe-food-type/recipe/${recipeId}/food_type/${foodTypeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
      })
    );

    // Create promises for adding new tag associations
    const addTagPromises = tagsToAdd.map(tagId =>
      fetchWithCSRF('/api/v1/recipe-tag', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ recipe_id: parseInt(recipeId), tag_id: tagId }),
      })
    );

    // Create promises for deleting tag associations
    const deleteTagPromises = tagsToDelete.map(tagId =>
      fetchWithCSRF(`/api/v1/recipe-tag/recipe/${recipeId}/tag/${tagId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
      })
    );

    // Wait for all add and delete operations to complete
    Promise.all([
      ...addCuisinePromises, 
      ...deleteCuisinePromises, 
      ...addDietPromises, 
      ...deleteDietPromises,
      ...addMealTimePromises,
      ...deleteMealTimePromises,
      ...addFoodTypePromises,
      ...deleteFoodTypePromises,
      ...addTagPromises,
      ...deleteTagPromises
    ])
      .then(() => {
        // console.log('Recipe associations updated successfully!');
        //if error, show error message
        setInitialCuisines([...selectedCuisines]);
        setInitialDiets([...selectedDiets]);
        setInitialMealTimes([...selectedMealTimes]);
        setInitialFoodTypes([...selectedFoodTypes]);
        setInitialTags([...selectedTags]);
        setChangesSavedMessage('Changes saved');
      })
      .catch(error => {
        console.error('Error updating recipe associations:', error);
        alert('An error occurred while updating the recipe associations');
      });
  };

  const filteredIngredients = allIngredients.filter(ingredient =>
    ingredient.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div style={styles.recipeEditContainer}>
        <h1 style={styles.recipeEditTitle}>Loading...</h1>
      </div>
    );
  }

  return (
    <div style={styles.recipeEditContainer}>
      <h1 style={styles.recipeEditTitle}>Editing {recipeName}</h1>
      <form onSubmit={handleSubmit} style={styles.recipeEditForm}>
        <div style={styles.formSection}>
          <h2 style={styles.sectionTitle}>Image</h2>
          <div style={styles.formGroup}>
            {/* Current Image Display */}
            <div style={{ marginBottom: '1rem' }}>
              {/* <h3 style={{ fontSize: '1rem', color: '#666', marginBottom: '0.5rem' }}>Current Image:</h3> */}
              <img 
                src={currentImageSrc}
                onError={(e) => {
                  // If we have a Supabase URL, use it immediately
                  if (supabaseImageUrl) {
                    console.log('Switching to Supabase image:', supabaseImageUrl);
                    setCurrentImageSrc(supabaseImageUrl);
                  }
                }}
                alt={recipeName}
                style={{ 
                  width: '200px',
                  height: '150px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '1px solid #ddd'
                }}
              />
            </div>
            
            <div>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                    Upload New Image
                    <input
                      type="file"
                      onChange={handleFileSelect}
                      style={styles.formInput}
                      accept="image/jpeg,image/png"
                      disabled={isUploading}
                    />
                  </label>
                </div>
                {selectedFile && (
                  <button
                    type="button"
                    onClick={handleImageUpload}
                    disabled={isUploading}
                    style={{
                      ...styles.addButton,
                      minWidth: '100px',
                      height: '40px',
                      marginBottom: '2px' // Align with input
                    }}
                  >
                    {isUploading ? 'Uploading...' : 'Upload'}
                  </button>
                )}
              </div>
              {uploadError && (
                <div style={{ color: '#dc3545', fontSize: '0.9rem' }}>
                  Error: {uploadError}
                </div>
              )}
              {selectedFile && !uploadError && !isUploading && (
                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                  Selected: {selectedFile.name}
                </div>
              )}
            </div>

            {/* AI Image Generation - Admin Only */}
            {isAdmin && (
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #eee' }}>
                <h3 style={{ fontSize: '1rem', color: '#666', marginBottom: '0.75rem' }}>AI Image Generation</h3>
                <div style={{ marginBottom: '0.75rem' }}>
                  <input
                    type="text"
                    placeholder="Optional: e.g., 'in a cast iron skillet', 'overhead shot', 'on a wooden board'..."
                    value={imageNote}
                    onChange={(e) => setImageNote(e.target.value)}
                    style={{
                      ...styles.formInput,
                      marginBottom: '0.5rem'
                    }}
                    className="form-control-custom"
                  />
                  <p style={{ fontSize: '0.8rem', color: '#888', margin: 0 }}>
                    Add a note to customize the image (presentation, angle, setting, etc.)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleGenerateAIImage}
                  disabled={isGeneratingAIImage}
                  style={{
                    ...styles.addButton,
                    backgroundColor: '#6366f1',
                    minWidth: '180px',
                    opacity: isGeneratingAIImage ? 0.7 : 1
                  }}
                >
                  {isGeneratingAIImage ? 'Generating...' : 'Generate AI Image'}
                </button>
                {aiImageError && !showAIImageModal && (
                  <div style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    Error: {aiImageError}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div style={styles.formSection}>
          <h2 style={styles.sectionTitle}>Nutrition Information</h2>


          <div style={styles.nutritionGrid}>
            <div style={styles.formGroup}>
              <label>
                Calories
                <input
                  type="number"
                  value={calories ? Math.round(calories) : ''}
                  onChange={e => setCalories(e.target.value)}
                  style={styles.formInput}
                  className="form-control-custom"
                />
              </label>
            </div>

            <div style={styles.formGroup}>
              <label>
                Protein (g)
                <input
                  type="number"
                  value={protein ? Math.round(protein) : ''}
                  onChange={e => setProtein(e.target.value)}
                  style={styles.formInput}
                  className="form-control-custom"
                />
              </label>
            </div>

            <div style={styles.formGroup}>
              <label>
                Carbohydrate (g)
                <input
                  type="number"
                  value={carbohydrate ? Math.round(carbohydrate) : ''}
                  onChange={e => setCarbohydrate(e.target.value)}
                  style={styles.formInput}
                  className="form-control-custom"
                />
              </label>
            </div>

            <div style={styles.formGroup}>
              <label>
                Fat (g)
                <input
                  type="number"
                  value={fat ? Math.round(fat) : ''}
                  onChange={e => setFat(e.target.value)}
                  style={styles.formInput}
                  className="form-control-custom"
                />
              </label>
            </div>
          </div>
        </div>

        <div style={styles.formSection}>
          <h2 style={styles.sectionTitle}>Ingredients</h2>
          <div style={styles.ingredientsList}>
            {ingredients.map(ingredient => (
              <div key={ingredient.ingredient_id} style={styles.ingredientItem}>
                <span>{ingredient.ingredient_name}: {ingredient.quantity} {ingredient.unit_of_measurement} {ingredient.notes}</span>
                <button 
                  onClick={() => removeIngredient(ingredient.ingredient_id)}
                  style={styles.removeButton}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div style={styles.ingredientSearchContainer}>
            
            
            <input
              type="text"
              placeholder={tempSelectedIngredient ? "Search for a different ingredient..." : "Search ingredients..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
              className="form-control-custom"
            />

          {tempSelectedIngredient && (
              <div style={styles.selectedIngredient}>
                <span>Selected: <strong>{tempSelectedIngredient}</strong></span>
                <button 
                  onClick={() => {
                    setTempSelectedIngredient('');
                    setNewIngredient({ 
                      ingredient_id: '', 
                      ingredient_name: '', 
                      quantity: '', 
                      notes: '',
                      unit_of_measurement: ''
                    });
                  }}
                  style={styles.clearButton}
                >
                  ×
                </button>
              </div>
            )}
            
            {searchTerm && (
              <div style={styles.searchResults}>
                {filteredIngredients.map(ingredient => (
                  <div
                    key={ingredient.id}
                    onClick={() => {
                      setNewIngredient({
                        ...newIngredient,
                        ingredient_id: ingredient.id,
                        ingredient_name: ingredient.name
                      });
                      setSearchTerm('');
                      setTempSelectedIngredient(ingredient.name);
                    }}
                    style={ingredient.name === tempSelectedIngredient ? styles.searchResultItemSelected : styles.searchResultItem}
                  >
                    {ingredient.name} ({ingredient.unit_of_measurement})
                  </div>
                ))}
                {filteredIngredients.length === 0 && (
                  <div style={styles.noResults}>No ingredients found</div>
                )}
              </div>
            )}
          </div>

          <div style={styles.ingredientForm}>
            <input
              type="text"
              placeholder="Quantity"
              value={newIngredient.quantity || ''}
              onChange={e => setNewIngredient({ ...newIngredient, quantity: e.target.value })}
              style={styles.formInput}
              className="form-control-custom"
            />
            <input
              type="text"
              placeholder="Notes"
              value={newIngredient.notes || ''}
              onChange={e => setNewIngredient({ ...newIngredient, notes: e.target.value })}
              style={styles.formInput}
              className="form-control-custom"
            />
            <button type="button" onClick={addIngredient} style={styles.addButton}>
              Add Ingredient
            </button>
          </div>
        </div>

        <div style={styles.formSection}>
          <h2 style={styles.sectionTitle}>Steps</h2>
          <ol style={styles.stepsList}>
            {[...steps]
              .sort((a, b) => a.step_number - b.step_number)
              .map(step => (
                <li key={`step-${step.step_number}`} style={styles.stepItem}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                    <span style={{ fontWeight: 'bold', minWidth: '3rem' }}>{step.step_number}.</span>
                    <span>{step.instruction}</span>
                  </div>
                  <button onClick={() => removeStep(step.id)} style={styles.removeButton}>
                    Remove
                  </button>
                </li>
              ))}
          </ol>
          <div style={styles.stepForm}>
            <input
              type="text"
              placeholder={`Step Number (next: ${getNextStepNumber()})`}
              value={newStep.stepNumber || ''}
              onChange={e => setNewStep({ ...newStep, stepNumber: e.target.value })}
              style={styles.formInput}
              className="form-control-custom"
            />
            <input
              type="text"
              placeholder="Instruction"
              value={newStep.instruction || ''}
              onChange={e => setNewStep({ ...newStep, instruction: e.target.value })}
              style={styles.formInput}
              className="form-control-custom"
            />
            <button type="button" onClick={addStep} style={styles.addButton}>
              Add Step
            </button>
          </div>
        </div>

        <div style={styles.formSection}>
          <div style={styles.categoriesGrid}>
            <div style={styles.categorySection}>
              <h2 style={styles.sectionTitle}>Cuisines</h2>
              <div style={styles.checkboxGrid}>
                {cuisines.map(cuisine => (
                  <button
                    key={cuisine.id}
                    type="button"
                    onClick={() => handleCuisineChange({ target: { value: cuisine.id, checked: !selectedCuisines.includes(cuisine.id) } })}
                    style={{
                      ...styles.selectionButton,
                      ...(selectedCuisines.includes(cuisine.id) ? styles.selectedButton : {})
                    }}
                  >
                    <img 
                      src={`/icons/cuisineOptions/${cuisine.name.replaceAll(' ', '')}.svg`}
                      alt={cuisine.name}
                      style={styles.buttonIcon}
                    />
                    {cuisine.name}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.categorySection}>
              <h2 style={styles.sectionTitle}>Diets</h2>
              <div style={styles.checkboxGrid}>
                {diets.map(diet => {
                  const dietName = diet.name.replaceAll(' ', '');
                  return (
                    <button
                      key={diet.id}
                      type="button"
                      onClick={() => handleDietChange({ target: { value: diet.id, checked: !selectedDiets.includes(diet.id) } })}
                      style={{
                        ...styles.selectionButton,
                        ...(selectedDiets.includes(diet.id) ? styles.selectedButton : {})
                      }}
                    >
                      <img 
                        src={`/icons/dietOptions/${dietName}.jpg`}
                        alt={diet.name}
                        style={styles.buttonIcon}
                      />
                      {diet.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={styles.categorySection}>
              <h2 style={styles.sectionTitle}>Meal Times</h2>
              <div style={styles.checkboxGrid}>
                {mealTimes.map(mealTime => {
                  const mealTimeEmoji = {
                    'Breakfast': '🍳',
                    'Lunch': '🥪',
                    'Dinner': '🍽️',
                    'Snack': '🥕'
                  };
                  return (
                    <button
                      key={mealTime.id}
                      type="button"
                      onClick={() => handleMealTimeChange({ target: { value: mealTime.id, checked: !selectedMealTimes.includes(mealTime.id) } })}
                      style={{
                        ...styles.selectionButton,
                        ...(selectedMealTimes.includes(mealTime.id) ? styles.selectedButton : {})
                      }}
                    >
                      <span style={{ fontSize: '1.2rem' }}>{mealTimeEmoji[mealTime.id] || ''}</span>
                      {mealTime.id}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={styles.categorySection}>
              <h2 style={styles.sectionTitle}>Food Types</h2>
              <div style={styles.checkboxGrid}>
                {foodTypes.map(foodType => (
                  <button
                    key={foodType.id}
                    type="button"
                    onClick={() => handleFoodTypeChange({ target: { value: foodType.id, checked: !selectedFoodTypes.includes(foodType.id) } })}
                    style={{
                      ...styles.selectionButton,
                      ...(selectedFoodTypes.includes(foodType.id) ? styles.selectedButton : {})
                    }}
                  >
                    <img 
                      src={`/icons/foodTypeOptions/${foodType.name}.png`}
                      alt={foodType.name}
                      style={styles.buttonIcon}
                    />
                    {foodType.name}
                  </button>
                ))}
              </div>
            </div>
            {isAdmin && (
              <div style={styles.categorySection}>
                <h2 style={styles.sectionTitle}>Tags</h2>
              <div style={styles.checkboxGrid}>
                {tags.map(tag => {
                  const isChecked = selectedTags.includes(tag.id);
                  return (
                    <label key={tag.id} style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        value={tag.id}
                        checked={isChecked}
                        onChange={handleTagChange}
                      />
                      {tag.name}
                    </label>
                  );
                })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={styles.formActions}>
          {/* <button type="button" onClick={updateRecipe} style={styles.updateButton}>
            Update Recipe
          </button> */}
          <button type="submit" style={styles.saveButton}>
            Save Changes
          </button>
          {changesSavedMessage && (
            <p style={styles.successMessage}>{changesSavedMessage}</p>
          )}
        </div>
      </form>

      {/* AI Image Preview Modal */}
      {showAIImageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '1rem', color: '#333' }}>
              AI Generated Image Preview
            </h2>
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              Review the generated image. Click "Approve & Upload" to save it to S3, or "Regenerate" to try again.
            </p>
            
            {aiImagePreview && (
              <>
                <img
                  src={`data:image/png;base64,${aiImagePreview}`}
                  alt="AI Generated Preview"
                  style={{
                    width: '100%',
                    borderRadius: '8px',
                    marginBottom: '0.5rem',
                    border: '1px solid #ddd'
                  }}
                />
                <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '1rem' }}>
                  File size: {(aiImagePreview.length * 0.75 / 1024 / 1024).toFixed(2)} MB (PNG, will be converted to JPEG on upload)
                </p>
              </>
            )}
            
            {aiImageError && (
              <div style={{ color: '#dc3545', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Error: {aiImageError}
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleCloseAIImageModal}
                disabled={isUploadingAIImage}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGenerateAIImage}
                disabled={isGeneratingAIImage || isUploadingAIImage}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#6366f1',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  opacity: (isGeneratingAIImage || isUploadingAIImage) ? 0.7 : 1
                }}
              >
                {isGeneratingAIImage ? 'Generating...' : 'Regenerate'}
              </button>
              <button
                type="button"
                onClick={handleApproveAIImage}
                disabled={isUploadingAIImage || isGeneratingAIImage}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#52cd92',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  opacity: (isUploadingAIImage || isGeneratingAIImage) ? 0.7 : 1
                }}
              >
                {isUploadingAIImage ? 'Uploading...' : 'Approve & Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecipeEdit;
